import React from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, Facebook, MapPin, Lock } from "lucide-react";
import { LOGOS } from "../lib/brand";
import { ScallopedBadge } from "./Decor2";
import { WavyLine } from "./Wavy";
import { Sparkle } from "./Decor2";
import { useSection } from "../context/SiteContext";

const Footer = () => {
  const { data: footer } = useSection("footer");

  return (
    <footer data-testid="site-footer" className="relative bg-cream text-plum-deep pt-16 pb-12 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-14">
          <WavyLine color="#EC2675" className="flex-1" />
          <Sparkle className="w-4 h-4 shrink-0" color="#EC2675" />
          <WavyLine color="#4ECDE9" className="flex-1" />
        </div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-start gap-5">
              <ScallopedBadge fill="#FDFBF7" ring="#EC2675" scallops={20} className="w-24 h-24 shrink-0">
                <img src={LOGOS.bloomFoundry} alt="The Bloom Foundry" className="w-[82%] h-[82%] object-contain" />
              </ScallopedBadge>
              <div>
                <p className="font-serif-display uppercase text-2xl leading-snug max-w-[18rem] tracking-[0.04em]">
                  {footer.tagline || "CREATING BEAUTIFUL GARDEN PRODUCTS TOGETHER"}
                </p>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-serif-display uppercase tracking-[0.22em] text-sm text-magenta mb-5 font-semibold">
              LINKS
            </h4>
            <ul className="space-y-2.5 text-[15px]">
              {[
                ["FOUNDRY WORKSHOP", "/workshop"],
                ["TRACK YOUR ORDER", "/track"],
                ["GALLERY", "/gallery"],
                ["HERITAGE BLOOM CLUB", "/bloom-club"],
                ["CONTACT + ABOUT", "/contact"],
                ["TERMS AND CONDITIONS", "/terms"],
                ["FOUNDRY CONSOLE", "/console/login"],
              ].map(([label, to]) => (
                <li key={label}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="font-serif-display uppercase tracking-[0.1em] hover:text-magenta transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-serif-display uppercase tracking-[0.22em] text-sm text-magenta mb-5 font-semibold">
              CONTACT US
            </h4>
            <ul className="space-y-3 text-[15px]">
              <li className="flex items-center gap-3" data-testid="footer-email">
                <Mail className="w-4 h-4 text-magenta shrink-0" />
                {footer.email || "hello@thebloomfoundry.nz"}
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="w-4 h-4 text-magenta shrink-0" />
                {footer.instagram || "@thebloomfoundry"}
              </li>
              <li className="flex items-center gap-3">
                <Facebook className="w-4 h-4 text-magenta shrink-0" />
                {footer.facebook || "The Bloom Foundry"}
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-magenta shrink-0" />
                {footer.location || "New Zealand"}
              </li>
            </ul>
          </div>
        </div>

        <p className="mt-14 text-center font-serif-display text-xs tracking-[0.2em] uppercase text-plum-deep/60">
          © {new Date().getFullYear()} THE BLOOM FOUNDRY — HERITAGE DAHLIAS NZ × THE ETCHERY. ALL RIGHTS RESERVED.
        </p>

        <div className="mt-6 flex justify-center">
          <Link
            to="/console/login"
            data-testid="footer-admin-login"
            className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-6 py-3 font-serif-display uppercase text-xs tracking-[0.2em] font-semibold hover:bg-magenta transition-colors"
          >
            <Lock className="w-3.5 h-3.5" /> ADMIN LOGIN
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
